// ========== 订单状态（后端 status 码按文档 §6.2 保留六值，页面按 4 个业务阶段展示） ==========
// 后端 status：10待出库 / 20租赁中 / 30待验机 / 40待结算 / 50已完成 / 90已取消
// 展示层把细状态归一为 4 阶段：待发货(10) / 租赁中(20) / 待收货(30+40) / 已归档(50)。
// 「待收货」阶段内拆成两个子状态（对齐原型 acceptance-workspace）：
//   · 30 确认收货：设备已回收，操作「确认收货」后进入验收结算；
//   · 40 验收结算：逐台验机 + 异常赔付 + 费用结算，提交后订单归档(50)。
// 列表/角标按多状态查询合并；已取消(90) 不属于四阶段，工作台不单列，仅作兜底标签保留。
export const ORDER_STATUS = {
  PENDING_OUTBOUND: 10,
  RENTING: 20,
  PENDING_INSPECTION: 30,
  PENDING_SETTLEMENT: 40,
  COMPLETED: 50,
  CANCELLED: 90,
};

// 对外展示的统一中文标签（对齐需求文档 §2「列表只保留待发货、租赁中、待收货、已归档四个状态」：
// 30/40 两个子状态统一展示为「待收货」，不再透出「确认收货/验收结算」细状态；
// 阶段内进度由收货验机弹窗承载，不改变订单 pill 文案）
export const ORDER_STATUS_LABEL = {
  [ORDER_STATUS.PENDING_OUTBOUND]: "待发货",
  [ORDER_STATUS.RENTING]: "租赁中",
  [ORDER_STATUS.PENDING_INSPECTION]: "待收货",
  [ORDER_STATUS.PENDING_SETTLEMENT]: "待收货",
  [ORDER_STATUS.COMPLETED]: "已归档",
  [ORDER_STATUS.CANCELLED]: "已取消",
};

// 色调对齐原型 order-status：待发货 amber / 租赁中 blue / 待收货 orange / 已归档 green / 已取消 gray
// 订单状态是固定的六档枚举，配色由前端字典维护（已确认不走后端字典接口，后端无需支持）
export const ORDER_STATUS_TONE = {
  [ORDER_STATUS.PENDING_OUTBOUND]: "amber",
  [ORDER_STATUS.RENTING]: "blue",
  [ORDER_STATUS.PENDING_INSPECTION]: "orange",
  [ORDER_STATUS.PENDING_SETTLEMENT]: "orange",
  [ORDER_STATUS.COMPLETED]: "green",
  [ORDER_STATUS.CANCELLED]: "neutral",
};

/** 把订单状态值（数字）转成中文标签；未知值原样返回 */
export const statusLabel = (s) => ORDER_STATUS_LABEL[Number(s)] || s;
/** 把订单状态值（数字）转成颜色色调（amber/green/red/neutral） */
export const statusTone = (s) => ORDER_STATUS_TONE[Number(s)] || "neutral";

// 「流转设置与人工调整」的可选目标状态（人工直接把订单调整到该状态）：
// 对齐页面的 4 个业务阶段（待发货 / 租赁中 / 待收货 / 已归档），其中「待收货」取阶段入口状态 30 确认收货
//（40 验收结算属于阶段内推进，不作为人工调整目标）；不含已取消(90)：取消属于订单取消流程，不在此处人工调整
export const ORDER_FLOW_STATUS_OPTIONS = [
  { value: ORDER_STATUS.PENDING_OUTBOUND, label: "待发货" },
  { value: ORDER_STATUS.RENTING, label: "租赁中" },
  { value: ORDER_STATUS.PENDING_INSPECTION, label: "待收货" },
  { value: ORDER_STATUS.COMPLETED, label: "已归档" },
];

// ========== 订单业务阶段（订单工作台 tab；对齐原型「待发货 / 租赁中 / 待收货 / 已归档」） ==========
// 后端状态是 6 个离散值，展示按「业务阶段」聚合为 4 个 tab，一个阶段可对应多个后端状态。
// 注意：不含「全部」与「已取消」——订单工作台默认 tab 即首个「待发货」
export const ORDER_STAGE = {
  PENDING_SHIP: "pending_ship", // 待发货：等配货与发货（后端 10）
  RENTING: "renting", // 租赁中：设备已交付（后端 20）
  RECEIVING: "receiving", // 待收货：待验机(30)+待结算(40) 归入同阶段
  ARCHIVED: "archived", // 已归档：结算归档（后端 50）
};

// 经营概览的时间口径（订单页「经营概览」卡片：今日 / 近7日 / 本月），与原型一致
export const ORDER_PERIODS = ["今日", "近7日", "本月"];

// tab 顺序即展示顺序；statuses 用于把阶段翻译成后端 OrderQuery.status 查询。
// 后端 OrderQuery.status 只接受单个状态值，跨状态阶段（待收货）需逐状态查询后合并。
export const ORDER_STAGE_OPTIONS = [
  {
    value: ORDER_STAGE.PENDING_SHIP,
    label: "待发货",
    statuses: [ORDER_STATUS.PENDING_OUTBOUND],
  },
  {
    value: ORDER_STAGE.RENTING,
    label: "租赁中",
    statuses: [ORDER_STATUS.RENTING],
  },
  {
    value: ORDER_STAGE.RECEIVING,
    label: "待收货",
    statuses: [
      ORDER_STATUS.PENDING_INSPECTION,
      ORDER_STATUS.PENDING_SETTLEMENT,
    ],
  },
  {
    value: ORDER_STAGE.ARCHIVED,
    label: "已归档",
    statuses: [ORDER_STATUS.COMPLETED],
  },
];

/** 后端订单状态 → 业务阶段（编辑订单按订单回填「当前状态」用）；未知状态回退「待发货」 */
export const stageOfStatus = (status) =>
  ORDER_STAGE_OPTIONS.find((o) => o.statuses.includes(Number(status)))?.value ||
  ORDER_STAGE.PENDING_SHIP;

// ========== 订单下一步动作（列表「操作」列与详情「当前待办」共用） ==========
export const ORDER_NEXT_ACTION = {
  OUTBOUND: "outbound", // 发货（待发货 10）
  RENEW: "renew", // 续租（租赁中 20）
  RECEIVE: "receive", // 收货验机与结算（待收货 · 确认收货 30；租赁中 20 的提前归还登记同入口）
  ACCEPT_SETTLE: "acceptSettle", // 收货验机与结算（待收货 · 验收结算 40；与 30 打开同一统一收货弹窗）
  DETAIL: "detail", // 终态：仅查看详情
};

export const ORDER_NEXT_ACTION_LABEL = {
  [ORDER_NEXT_ACTION.OUTBOUND]: "绑定并发货",
  [ORDER_NEXT_ACTION.RENEW]: "续租",
  [ORDER_NEXT_ACTION.RECEIVE]: "收货验机与结算",
  [ORDER_NEXT_ACTION.ACCEPT_SETTLE]: "收货验机与结算",
  [ORDER_NEXT_ACTION.DETAIL]: "查看详情",
};

/** 各状态下「当前待办」的说明文案（详情弹窗 compact-task 用） */
export const ORDER_NEXT_ACTION_DESC = {
  [ORDER_NEXT_ACTION.OUTBOUND]: "先绑定具体设备并完成出库检查。",
  [ORDER_NEXT_ACTION.RENEW]: "租赁履约中；也可以提前登记客户归还。",
  [ORDER_NEXT_ACTION.RECEIVE]:
    "收到设备后验机一次，核对费用并归档；支持分批保存。",
  [ORDER_NEXT_ACTION.ACCEPT_SETTLE]:
    "收到设备后验机一次，核对费用并归档；支持分批保存。",
  [ORDER_NEXT_ACTION.DETAIL]: "订单已完成归档，业务资料只读。",
};

/** 订单状态 → 下一步动作 key */
export const nextActionOfStatus = (status) =>
  ({
    [ORDER_STATUS.PENDING_OUTBOUND]: ORDER_NEXT_ACTION.OUTBOUND,
    [ORDER_STATUS.RENTING]: ORDER_NEXT_ACTION.RENEW,
    [ORDER_STATUS.PENDING_INSPECTION]: ORDER_NEXT_ACTION.RECEIVE,
    [ORDER_STATUS.PENDING_SETTLEMENT]: ORDER_NEXT_ACTION.ACCEPT_SETTLE,
    [ORDER_STATUS.COMPLETED]: ORDER_NEXT_ACTION.DETAIL,
    [ORDER_STATUS.CANCELLED]: ORDER_NEXT_ACTION.DETAIL,
  })[Number(status)] || ORDER_NEXT_ACTION.DETAIL;

/** 下一步动作 key → 中文标签 */
export const nextActionLabel = (v) => ORDER_NEXT_ACTION_LABEL[v] || "查看详情";

/** 下一步动作 key → 待办说明 */
export const nextActionDesc = (v) =>
  ORDER_NEXT_ACTION_DESC[v] || ORDER_NEXT_ACTION_DESC[ORDER_NEXT_ACTION.DETAIL];

// ========== 订单终态（资料只读，不再展示「当前待办」） ==========
export const ORDER_FINAL_STATUS = [
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.CANCELLED,
];

/** 订单状态是否为终态（已归档 / 已取消） */
export const isFinalOrderStatus = (s) => ORDER_FINAL_STATUS.includes(Number(s));

// ========== 订单操作权限（详情弹窗各区块按钮：按订单状态判定） ==========
export const ORDER_ACTION = {
  EDIT: "edit", // 编辑订单：重新打开录单弹窗修改订单资料（客户 / 交付 / 租赁与费用）
  CONFIRM_OUTBOUND: "confirmOutbound", // 确认发货
};

// 各操作允许的状态集合（已取消 90 一律不允许）
export const ORDER_ACTION_STATUS = {
  // 编辑订单：待收货两个子状态（30 确认收货 / 40 验收结算）也允许改资料；已归档(50) 资料只读
  [ORDER_ACTION.EDIT]: [
    ORDER_STATUS.PENDING_OUTBOUND,
    ORDER_STATUS.RENTING,
    ORDER_STATUS.PENDING_INSPECTION,
    ORDER_STATUS.PENDING_SETTLEMENT,
  ],
  [ORDER_ACTION.CONFIRM_OUTBOUND]: [ORDER_STATUS.PENDING_OUTBOUND],
};

/** 订单在指定状态下是否允许某操作 */
export const canOrderAction = (status, action) =>
  Number(status) !== ORDER_STATUS.CANCELLED &&
  (ORDER_ACTION_STATUS[action] || []).includes(Number(status));

// ========== 设备状态数字编码（设备台账模块 Device.status；10在库/20待出库/30租赁中/40待验机/50维修中/90停用） ==========
export const DEVICE_STATUS_CODE = {
  IDLE: 10,
  PENDING_OUTBOUND: 20,
  RENTING: 30,
  PENDING_INSPECTION: 40,
  REPAIR: 50,
  SOLD: 60,
  DISABLED: 90,
};

// 设备状态的中文名与配色一律走后端字典 GET /v1/dict/device-statuses
// （composables/useDeviceStatuses.js 单例）：文案 / 配色由后端下发，前端不维护映射表。

// ========== 订单设备绑定状态（文档 §6.2 订单设备 bindStatus / deviceBindStatus） ==========
// 1=临时绑定 2=已出库 3=已回收 4=未归还 9=已释放
export const DEVICE_BIND_STATUS = {
  TEMP: 1,
  OUTBOUND: 2,
  RETURNED: 3,
  UNRETURNED: 4,
  RELEASED: 9,
};

export const DEVICE_BIND_STATUS_LABEL = {
  [DEVICE_BIND_STATUS.TEMP]: "临时绑定",
  [DEVICE_BIND_STATUS.OUTBOUND]: "已出库",
  [DEVICE_BIND_STATUS.RETURNED]: "已回收",
  [DEVICE_BIND_STATUS.UNRETURNED]: "未归还",
  [DEVICE_BIND_STATUS.RELEASED]: "已释放",
};

/** 订单设备绑定状态数字 → 中文标签；接口优先透出 bindStatusName，未知值回退「已绑定」 */
export const deviceBindStatusLabel = (v) =>
  DEVICE_BIND_STATUS_LABEL[Number(v)] || "已绑定";

// ========== 角色（文档 §2.5） ==========
export const ROLE = {
  BOSS: "BOSS",
  PARTNER: "PARTNER",
};

// ========== 订单来源（中文 UI；后端真实 sourceId 需后端给枚举表） ==========
// 文档 §6.2 入参 sourceId 暗示后端有"来源表"返回 ID；此处仅维护 UI 选项
export const SOURCE = {
  XIANYU: "xianyu",
  WECHAT: "wechat",
  XIAOHONGSHU: "xiaohongshu",
  DOUYIN: "douyin",
  RENRENZU: "renrenzu",
  OFFLINE: "offline",
};

export const SOURCE_LABEL = {
  [SOURCE.XIANYU]: "闲鱼",
  [SOURCE.WECHAT]: "微信",
  [SOURCE.XIAOHONGSHU]: "小红书",
  [SOURCE.DOUYIN]: "抖音",
  [SOURCE.RENRENZU]: "人人租",
  [SOURCE.OFFLINE]: "线下",
};

export const SOURCE_MARK = {
  [SOURCE.XIANYU]: "闲",
  [SOURCE.WECHAT]: "微",
  [SOURCE.XIAOHONGSHU]: "红",
  [SOURCE.DOUYIN]: "抖",
  [SOURCE.RENRENZU]: "租",
  [SOURCE.OFFLINE]: "线",
};

const _SOURCE_REVERSE = Object.fromEntries(
  Object.entries(SOURCE_LABEL).map(([k, v]) => [v, k]),
);

// ========== 履约方式（后端 DeliveryMethodType 枚举；订单 deliveryType 字段） ==========
// EXPRESS(1,"快递") / CITY_DELIVERY(2,"同城送") / SELF_PICKUP(3,"客户自提") / DOOR_RECYCLE(4,"上门回收")
// 录入订单弹窗固定展示前三种（快递/同城送/客户自提），4=上门回收 仅作展示兜底保留
export const DELIVERY_TYPE = {
  EXPRESS: 1,
  CITY_DELIVERY: 2,
  SELF_PICKUP: 3,
  DOOR_RECYCLE: 4,
};

export const DELIVERY_TYPE_LABEL = {
  [DELIVERY_TYPE.EXPRESS]: "快递",
  [DELIVERY_TYPE.CITY_DELIVERY]: "同城送",
  [DELIVERY_TYPE.SELF_PICKUP]: "客户自提",
  [DELIVERY_TYPE.DOOR_RECYCLE]: "上门回收",
};

// ========== 押金方式（订单模块 depositType 字段；10=现金/转账押金 20=手工免押；90=其他已忽略） ==========
export const DEPOSIT_TYPE = {
  CASH: 10,
  MANUAL_EXEMPT: 20,
};

export const DEPOSIT_TYPE_LABEL = {
  [DEPOSIT_TYPE.CASH]: "押金收取",
  [DEPOSIT_TYPE.MANUAL_EXEMPT]: "支付宝免押",
};

/** 押金方式数字 → 中文标签；未知值返回 '—' */
export const depositTypeLabel = (v) => DEPOSIT_TYPE_LABEL[Number(v)] || "—";

// ========== 录单弹窗押金方式（新版口径固定两档；兼容历史「押金收取 / 支付宝免押 / 线下免押」展示） ==========
// 免押码 = 免押额度（不计现金退款）；线下押金 = 实际收取现金。
export const DEPOSIT_MODE = {
  EXEMPT: "免押码",
  OFFLINE: "线下押金",
};
export const DEPOSIT_MODE_OPTIONS = [
  { value: DEPOSIT_MODE.EXEMPT, label: DEPOSIT_MODE.EXEMPT },
  { value: DEPOSIT_MODE.OFFLINE, label: DEPOSIT_MODE.OFFLINE },
];
/** UI 押金方式 → 后端 depositType：「线下押金」为现金押金(10)，「免押码」为手工免押(20) */
export const depositTypeOf = (mode) =>
  mode === DEPOSIT_MODE.OFFLINE
    ? DEPOSIT_TYPE.CASH
    : DEPOSIT_TYPE.MANUAL_EXEMPT;

// ========== 订单来源（来源字典由后端 GET /v1/admin/order-sources 下发，前端不再固定枚举） ==========
// 历史「闲管家」等旧来源在编辑回填时归一为「闲鱼」，其余未知来源归一为「其他」。
/** 历史来源名归一（仅编辑回填兜底；新来源直接以后端字典为准） */
export const normalizeOrderSource = (name) => {
  if (!name) return "";
  if (String(name).includes("闲管家")) return "闲鱼";
  return "其他";
};

// ========== 录单 / 收货弹窗共用字典（后端暂无字典接口，前端统一维护） ==========
// 发货方式固定三档（原型录单口径；4=上门回收仅作列表兜底，录单不展示）
export const DELIVERY_OPTIONS = [
  {
    value: DELIVERY_TYPE.EXPRESS,
    label: DELIVERY_TYPE_LABEL[DELIVERY_TYPE.EXPRESS],
  },
  {
    value: DELIVERY_TYPE.CITY_DELIVERY,
    label: DELIVERY_TYPE_LABEL[DELIVERY_TYPE.CITY_DELIVERY],
  },
  {
    value: DELIVERY_TYPE.SELF_PICKUP,
    label: DELIVERY_TYPE_LABEL[DELIVERY_TYPE.SELF_PICKUP],
  },
];

// 注：录单弹窗的发货方式已改为读 GET /v1/admin/delivery-methods（按 methodName 展示具体方式名，
// 如「顺丰速运」），不再按类型本地拼文案；DELIVERY_TYPE_LABEL 只用于「接口没给 methodName /
// 列表详情按 deliveryTypeName 缺失」时的兜底。

// 履约确认（录单「03 当前状态补充」）
export const FULFILLMENT_STATUS = { DONE: "已完成", PENDING: "待补充" };
export const FULFILLMENT_OPTIONS = [
  { value: FULFILLMENT_STATUS.DONE, label: FULFILLMENT_STATUS.DONE },
  { value: FULFILLMENT_STATUS.PENDING, label: FULFILLMENT_STATUS.PENDING },
];

// 归还方式（录单「04 归还信息」与收货弹窗共用）
export const RETURN_METHOD = {
  STORE: "到店归还",
  EXPRESS: "快递归还",
  CITY_PICKUP: "同城取回",
};
export const RETURN_METHOD_OPTIONS = [
  { value: RETURN_METHOD.STORE, label: RETURN_METHOD.STORE },
  { value: RETURN_METHOD.EXPRESS, label: RETURN_METHOD.EXPRESS },
  { value: RETURN_METHOD.CITY_PICKUP, label: RETURN_METHOD.CITY_PICKUP },
];

// 验机结论统一由后端字典 GET /v1/dict/inspection-conclusions 提供（composables/useInspectConclusions.js），
// 前端不保留任何常量兜底：接口未返回时下拉无可选项。
// 接口层 INSPECT_RESULT 的 1通过 / 2异常 仍是提交口径，由字典项的 abnormal 映射。

// 登记无法归还弹窗的丢机原因（新版口径固定四档）
export const LOST_REASONS = ["设备丢失", "租客失联", "拒绝归还", "其他"];

/** 丢机原因 → 后端 reason 编码（POST /v1/orders/{id}/lost） */
export const LOST_REASON_CODE = {
  设备丢失: "LOST",
  租客失联: "CONTACT_LOST",
  拒绝归还: "REFUSE_RETURN",
  其他: "OTHER",
};

// 结算状态（录单「05 验机与结算」）
export const SETTLE_STATUS = { SETTLED: "已结算", PENDING: "待处理" };
export const SETTLE_STATUS_OPTIONS = [
  { value: SETTLE_STATUS.SETTLED, label: SETTLE_STATUS.SETTLED },
  { value: SETTLE_STATUS.PENDING, label: SETTLE_STATUS.PENDING },
];

// 单次租期天数：默认 3 天，上限 90 天
export const RENT_DAYS = { DEFAULT: 3, MAX: 90 };

// 录单弹窗日期控件统一口径（Element date-picker，精确到日；01/03/04 等子区块组件共用）
export const ORDER_DATE_PICKER_PROPS = {
  type: "date",
  valueFormat: "YYYY-MM-DD",
  format: "YYYY/MM/DD",
  placeholder: "年/月/日",
  editable: false,
};
// 租期区间（开始 ~ 预计归还）：必选且不允许手工输入，与「租赁天数」联动
export const ORDER_RANGE_PICKER_PROPS = {
  type: "daterange",
  valueFormat: "YYYY-MM-DD",
  format: "YYYY/MM/DD",
  clearable: false,
  editable: false,
};

// 订单阶段的展示补充信息（录单弹窗：阶段说明 / 状态提示 / 摘要药丸色调）
export const ORDER_STAGE_META = {
  [ORDER_STAGE.PENDING_SHIP]: {
    desc: "订单已录入，发货时统一完成设备绑定与交付信息确认。",
    statusNote: "",
    tone: "pending",
  },
  [ORDER_STAGE.RENTING]: {
    desc: "设备已交付，跟进租期与归还。",
    statusNote: "只填写让“租赁中”可继续流转的最少信息。",
    tone: "renting",
  },
  [ORDER_STAGE.RECEIVING]: {
    desc: "租期已结束或设备已收回，继续完成收货、验机与费用结算。",
    statusNote: "只填写让“待验机”可继续流转的最少信息。",
    tone: "returning",
  },
  [ORDER_STAGE.ARCHIVED]: {
    desc: "历史订单已结算并归档。",
    statusNote: "只填写让“已完成”可继续流转的最少信息。",
    tone: "archived",
  },
};

/** 阶段值 → { label, desc, statusNote, tone }；未知阶段回落「待发货」 */
export const stageMetaOf = (stage) => ({
  label:
    ORDER_STAGE_OPTIONS.find((s) => s.value === stage)?.label ||
    ORDER_STATUS_LABEL[ORDER_STATUS.PENDING_OUTBOUND],
  ...(ORDER_STAGE_META[stage] || ORDER_STAGE_META[ORDER_STAGE.PENDING_SHIP]),
});

/** UI 标签 → 单字 mark */
export const sourceMark = (label) => {
  const key = _SOURCE_REVERSE[label];
  return key ? SOURCE_MARK[key] : "其";
};

// ========== 启用状态（设备/履约方式/订单来源等所有管理模块通用；1=启用 0=停用） ==========
export const STATUS = {
  ENABLED: 1,
  DISABLED: 0,
};

export const STATUS_LABEL = {
  [STATUS.ENABLED]: "已启用",
  [STATUS.DISABLED]: "已停用",
};

/** 启用状态值 → 中文标签（启用/停用）；未知值返回 '—' */
export const enableStatusLabel = (v) => STATUS_LABEL[Number(v)] || "—";

// ========== 设备图标 ==========
/** 根据设备型号名推断图标类型：口袋机→pocket，镜头→lens，其余→camera */
export const thumbType = (n) => {
  if (!n) return "pocket";
  const s = n.toLowerCase();
  if (s.includes("pocket")) return "pocket";
  if (s.includes("70-200") || s.includes("24-70") || s.includes("16-35"))
    return "lens";
  return "camera";
};

// ========== 订单 itemType（文档 §6.2） ==========
// 1=型号 2=套餐
export const ITEM_TYPE = {
  MODEL: 1,
  PACKAGE: 2,
};

// ========== 设备登记流转 action（设备流转模块，登记设备流转弹窗） ==========
// 1=送修 2=维修完成 3=出售
export const FLOW_ACTION = {
  REPAIR: 1,
  REPAIR_COMPLETE: 2,
  SOLD: 3,
};

export const FLOW_ACTION_OPTIONS = [
  { value: FLOW_ACTION.REPAIR, label: "送修" },
  { value: FLOW_ACTION.REPAIR_COMPLETE, label: "维修完成" },
  { value: FLOW_ACTION.SOLD, label: "出售" },
];

// ========== 打印记录 printType（打印记录上报模块） ==========
// 1=设备标签 2=交货单
export const PRINT_TYPE = {
  LABEL: 1,
  DELIVERY: 2,
};

// ========== 打印记录 scope（打印记录上报模块） ==========
// 1=单台 2=批量
export const PRINT_SCOPE = {
  SINGLE: 1,
  BATCH: 2,
};

// ========== 成员管理（成员列表 / 加入申请审核 / 管理抽屉） ==========
// 名额 used / quota 来自 GET /v1/admin/members/quota，角色名来自 GET /v1/admin/members/roles，
// 店铺名 / 店铺号来自 GET /v1/shops：三处均由接口下发，不再内置常量兜底（缺失显示「—」）

// 角色码（PUT /v1/admin/members/{id} 的 role 传角色码；展示用中文 label 走角色字典）
// 后端已确认的角色码前缀为 BOSS / PARTNER / CLERK…，此处按后端约定内置，未知码直接展示原码
export const MEMBER_ROLE = {
  BOSS: "BOSS",
  PARTNER: "PARTNER",
  MANAGER: "MANAGER",
  CLERK: "CLERK",
  WAREHOUSE: "WAREHOUSE",
  FINANCE: "FINANCE",
};

export const MEMBER_ROLE_OPTIONS = [
  { code: MEMBER_ROLE.BOSS, label: "超级管理员" },
  { code: MEMBER_ROLE.PARTNER, label: "合伙人" },
  { code: MEMBER_ROLE.MANAGER, label: "管理员" },
  { code: MEMBER_ROLE.CLERK, label: "店员" },
  { code: MEMBER_ROLE.WAREHOUSE, label: "仓库管理员" },
  { code: MEMBER_ROLE.FINANCE, label: "财务" },
];

/** 角色码 → 中文（未知码原样返回，避免页面出现空白） */
export const memberRoleLabel = (code) =>
  MEMBER_ROLE_OPTIONS.find((o) => o.code === code)?.label || code || "";

// ========== 设备成色（设备台账模块：单个录入 / 设备详情基本信息） ==========
// UI 展示文案，提交时按 grade 数字码传给后端 conditionGrade
export const DEVICE_CONDITION_GRADES = [
  { label: "全新", grade: 10 },
  { label: "99新", grade: 20 },
  { label: "95新", grade: 30 },
  { label: "9成新", grade: 40 },
  { label: "8成新", grade: 50 },
];

// ========== 验机 result（文档 §6.4） ==========
// 1=通过 2=异常
export const INSPECT_RESULT = {
  PASS: 1,
  EXCEPTION: 2,
};

// ========== 订单 tagCode（文档 §6.2 OrderQuery；DAMAGE_UNPAID 来自详情接口实际返回；RENEWED/PARTIAL_RETURN/ABNORMAL_LOST 为收货续租 mock 口径新增） ==========
export const ORDER_TAG = {
  OVERDUE: "OVERDUE",
  DAMAGE_UNPAID: "DAMAGE_UNPAID",
  RENEWED: "RENEWED",
  PARTIAL_RETURN: "PARTIAL_RETURN",
  ABNORMAL_LOST: "ABNORMAL_LOST",
};
/** 订单标签文案（列表「状态 / 提示」列直接展示） */
export const ORDER_TAG_LABEL = {
  [ORDER_TAG.OVERDUE]: "逾期未还",
  [ORDER_TAG.DAMAGE_UNPAID]: "损坏待赔付",
  [ORDER_TAG.RENEWED]: "已续租",
  [ORDER_TAG.PARTIAL_RETURN]: "部分归还",
  [ORDER_TAG.ABNORMAL_LOST]: "异常丢机",
};

// ========== 归档结论 archiveType（订单详情：终态订单是怎么结束的） ==========
// 订单进入终态（50 已完成 / 90 已取消）时写入；未归档（非终态）为 null
export const ARCHIVE_TYPE = {
  NORMAL: 1, // 正常完成
  CANCEL: 2, // 取消关闭
  ABNORMAL: 3, // 异常关闭（丢机 / 损坏未赔等）
};

/** 归档结论文案：仅异常关闭需要在详情顶部展示异常横幅 */
export const ARCHIVE_TYPE_LABEL = {
  [ARCHIVE_TYPE.NORMAL]: "正常完成",
  [ARCHIVE_TYPE.CANCEL]: "取消关闭",
  [ARCHIVE_TYPE.ABNORMAL]: "异常丢机",
};

// ========== 业务错误码（文档 §7） ==========
export const BIZ_CODE = {
  PARAM_INVALID: 10001,
  DATA_NOT_FOUND: 10002,
  UNLOGIN: 20001,
  NO_PERMISSION: 20002,
  CROSS_TENANT: 20003,
  KICKED: 20005,
  STOCK_NOT_ENOUGH: 30001,
  STOCK_CONFLICT: 30002,
  PACKAGE_COMP_NOT_ENOUGH: 30003,
  DEVICE_OCCUPIED: 30004,
  ORDER_STATUS_FORBID: 40001,
  ORDER_CANCELED: 40002,
  ORDER_ITEM_LOCKED: 40003,
  DEVICE_CODE_INVALID: 50001,
  DEVICE_CODE_VOID: 50002,
  DEVICE_NOT_IN_STOCK: 50003,
  MODEL_MISMATCH: 50004,
  DEVICE_DUP_SCAN: 50005,
  CROSS_MERCHANT_DEVICE: 50006,
  REQUIRED_ACCESSORY_UNDONE: 50007,
  INSPECT_ITEM_EMPTY: 60001,
  SETTLE_CONFIRMED: 60002,
  BOSS_ONLY: 60003,
  NAME_DUP: 70001,
  DISABLED_OBJECT: 70002,
};

export const BIZ_CODE_MESSAGE = {
  [BIZ_CODE.PARAM_INVALID]: "参数校验失败",
  [BIZ_CODE.DATA_NOT_FOUND]: "数据不存在",
  [BIZ_CODE.UNLOGIN]: "登录态过期",
  [BIZ_CODE.NO_PERMISSION]: "无权限",
  [BIZ_CODE.CROSS_TENANT]: "跨租户访问",
  [BIZ_CODE.KICKED]: "账号已在其他设备登录",
  [BIZ_CODE.STOCK_NOT_ENOUGH]: "库存不足",
  [BIZ_CODE.STOCK_CONFLICT]: "档期已被占用",
  [BIZ_CODE.PACKAGE_COMP_NOT_ENOUGH]: "套餐组件不足",
  [BIZ_CODE.DEVICE_OCCUPIED]: "设备已被占用",
  [BIZ_CODE.ORDER_STATUS_FORBID]: "当前状态不允许该操作",
  [BIZ_CODE.ORDER_CANCELED]: "订单已取消",
  [BIZ_CODE.ORDER_ITEM_LOCKED]: "明细不可编辑",
  [BIZ_CODE.DEVICE_CODE_INVALID]: "设备码无效",
  [BIZ_CODE.DEVICE_CODE_VOID]: "标签已作废",
  [BIZ_CODE.DEVICE_NOT_IN_STOCK]: "设备非在库",
  [BIZ_CODE.MODEL_MISMATCH]: "型号不匹配",
  [BIZ_CODE.DEVICE_DUP_SCAN]: "重复扫描",
  [BIZ_CODE.CROSS_MERCHANT_DEVICE]: "跨商家设备",
  [BIZ_CODE.REQUIRED_ACCESSORY_UNDONE]: "必检项未完成",
  [BIZ_CODE.INSPECT_ITEM_EMPTY]: "必检项未填",
  [BIZ_CODE.SETTLE_CONFIRMED]: "结算已确认",
  [BIZ_CODE.BOSS_ONLY]: "仅老板可操作",
  [BIZ_CODE.NAME_DUP]: "名称重复",
  [BIZ_CODE.DISABLED_OBJECT]: "对象已停用",
};

// ========== 工具函数 ==========
/** 把数字格式化为带千分位与人民币符号的金额字符串，如 1234.5 → ¥1,234.5 */
export const money = (v) =>
  "¥" + Number(v || 0).toLocaleString("zh-CN", { maximumFractionDigits: 2 });

/**
 * 后端金额为"分"，转换为"元"后再用 money() 格式化输出。
 * 接口未返回（null / undefined）显示「—」：不把缺失的金额当 0 展示。
 */
export const moneyFromCents = (v) => (v == null ? "—" : money(Number(v) / 100));

/** 后端金额为"分"，转换为"元"（纯数字，用于表单回显/计算） */
export const centsToYuan = (v) => Number(v || 0) / 100;

/** 前端表单以"元"填写，提交给后端前转换为"分"（后端统一存分） */
export const yuanToCents = (v) => Math.round((Number(v) || 0) * 100);

/**
 * 比较两个后端 id 是否相同。id 一律原样透传（后端 int64 / 雪花号可能超出 JS 安全整数范围，
 * Number() 会丢精度导致后端查不到记录），因此只在比较时转字符串，不做数值归一。
 */
export const isSameId = (a, b) =>
  a != null && b != null && String(a) === String(b);

/** 把 YYYY-MM-DD 日期压缩为「M月D日」短格式，空值返回空串 */
export const shortDate = (v) => {
  if (!v) return "";
  // 兼容 ISO 时间戳（2026-08-13T10:00:00）与纯日期（2026-08-13）
  const datePart = String(v).split("T")[0];
  const p = datePart.split("-");
  if (p.length === 3) return `${Number(p[1])}月${Number(p[2])}日`;
  return v;
};

/** 智能时间：当天 → 「今天 HH:mm」；非当天 → withYear ? 「YYYY年M月D日 HH:mm」 : 「M月D日 HH:mm」；空值返回空串 */
export const smartTime = (v, withYear = true) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const now = new Date();
  const pad2 = (n) => String(n).padStart(2, "0");
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `今天 ${hm}`;
  const date = withYear
    ? `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
    : `${d.getMonth() + 1}月${d.getDate()}日`;
  return `${date} ${hm}`;
};

/** 数字时间：YYYY-MM-DD HH:mm；空值返回空串（列表紧凑场景用，如订单页） */
export const dateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** 纯日期：YYYY-MM-DD；空值返回空串 */
export const dateOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** 纯时间：HH:mm；空值返回空串 */
export const timeOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(11, 16);
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** 今天（本地时区）YYYY-MM-DD */
export const todayStr = () => dateOnly(new Date());

/** 日期字符串（YYYY-MM-DD 或 ISO）偏移 n 天，返回 YYYY-MM-DD */
export const shiftDate = (v, n) => {
  const base = new Date(`${String(v || todayStr()).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + Number(n || 0));
  return dateOnly(base);
};

/** 两个日期相差天数（end - start），不足 1 天按 1 天算；任一端为空返回 0 */
export const daysBetween = (start, end) => {
  if (!start || !end) return 0;
  const a = new Date(`${String(start).slice(0, 10)}T00:00:00`);
  const b = new Date(`${String(end).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(1, Math.round((b - a) / 86400000));
};
